import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight, RefreshCw, ShoppingCart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDeliveryStatus, isBlockedDeliveryCode } from '@/lib/deliveryStatus';
import { mduAddress, declaredAddress } from '@/lib/apartmentAddress';
import { GlassCard } from '@/components/custom/GlassCard';
import { OpenOrderCart } from '@/components/tickets/OpenOrderCart';
import type { OpenOrdersState, SameAddressState } from '@/hooks/useAddressValidation';
import type { SameAddressUnitDto } from '@uniticket/shared';

// N41 (décisions du user des 07-10 et 08-10) — les logements qui portent la MÊME adresse postale qu'un logement de
// la fenêtre, mais que le DMP range sous un AUTRE building ID (ou sous aucun). Erreur de données probable : un
// logement créé à côté de son bâtiment. La fenêtre les MONTRE et le signale ; elle ne les compte pas — ils ne
// sont pas dans `mduApartments`, donc ni dans la capacité, ni dans la lecture COCON, ni dans le formulaire d'ajout.
//
// Trois morceaux, parce que la même liste s'affiche à deux endroits :
//  · `SameAddressRows` — les lignes à glisser sous une adresse, DANS le tableau des logements d'un bâtiment ;
//  · `SameAddressCard` — le même tableau, seul, quand la fenêtre n'en a pas (un logement seul dans son bâtiment) ;
//  · `SameAddressNotice` — ce que la lecture a à dire d'elle-même : elle a échoué, ou elle a été bornée.
// Les lignes ne s'ouvrent pas : la fiche d'un logement (release, mapping, statut) reste celle des logements du
// bâtiment du ticket.

/** Les mêmes huit colonnes que le tableau des logements — voir `APARTMENT_TABLE_COLS` dans la page. */
const COLS = 8;

function UnitRow({ u, twins, orders }: { u: SameAddressUnitDto; twins?: number[]; orders: OpenOrdersState }) {
  const { t } = useTranslation(['tickets']);
  const declared = declaredAddress(u);
  return (
    <tr className="border-t border-amber-100 bg-amber-50/60" title={t('tickets:validation.sameAddress.rowTitle')}>
      <td className="px-3 py-2 font-medium text-slate-800">
        {u.jvid}
        <span className="ml-2 rounded bg-amber-200/70 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800">
          {u.belmapBuildingId != null
            ? t('tickets:validation.sameAddress.building', { id: u.belmapBuildingId })
            : t('tickets:validation.sameAddress.noBuilding')}
        </span>
      </td>
      <td className="px-3 py-2 text-slate-600">
        <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          {declared ? (
            <span
              className="inline-flex items-center gap-1"
              title={t('tickets:validation.addressMappedTitle', { orig: mduAddress(u), declared: declared.full })}
            >
              <span className="text-slate-400">{mduAddress(u)}</span>
              <ArrowRight className="h-3 w-3 shrink-0 text-brand-rose" />
              <span className="font-semibold text-brand-rose">{declared.short}</span>
            </span>
          ) : mduAddress(u)}
          {twins && (
            <span
              className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-700"
              title={t('tickets:validation.declaredDup.rowTitle', { count: twins.length, jvids: twins.join(', ') })}
            >
              {t('tickets:validation.declaredDup.badge')}
            </span>
          )}
        </span>
      </td>
      <td className="px-3 py-2 text-slate-600">
        {u.mappedBusNumber != null ? (
          <span className="inline-flex items-center gap-1" title={t('tickets:validation.busMappedTitle', { orig: u.busNumber ?? '—' })}>
            <span className="text-slate-400">{u.busNumber ?? '—'}</span>
            <ArrowRight className="h-3 w-3 text-brand-rose" />
            {/* Une boîte déclarée VIDE se dit : sans le tiret, la flèche ne pointerait sur rien. */}
            <span className="font-semibold text-brand-rose">{u.mappedBusNumber || '—'}</span>
          </span>
        ) : (u.busNumber || '—')}
      </td>
      <td className="px-3 py-2">
        {u.status === 'HOMES_CONNECT' ? (
          <span className="inline-flex items-center gap-1 font-semibold text-red-700">
            <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-600 text-[9px] font-bold leading-none text-white">!</span>
            {u.status}
          </span>
        ) : (
          <span className="text-slate-600">{u.status ?? '—'}</span>
        )}
      </td>
      <td className="px-3 py-2 text-slate-600">
        {u.deliveryStatus != null ? (
          <span
            className={cn(
              'cursor-help underline decoration-dotted underline-offset-2',
              isBlockedDeliveryCode(Number(u.deliveryStatus)) ? 'font-semibold text-red-700 decoration-red-300' : 'decoration-slate-300',
            )}
            title={formatDeliveryStatus(Number(u.deliveryStatus))}
          >
            {u.deliveryStatus}
          </span>
        ) : '—'}
      </td>
      <td className="px-3 py-2 text-slate-600">{u.scopelistV15 ?? '—'}</td>
      <td className="px-3 py-2 text-slate-600">{u.scopelistV2 ?? '—'}</td>
      <td className="px-2 py-2 text-center">
        <OpenOrderCart state={orders} jvid={u.jvid} unitStatus={u.status} />
      </td>
    </tr>
  );
}

/** Les logements « d'ailleurs » d'UNE adresse, à rendre dans le `<tbody>` du tableau des logements. */
export function SameAddressRows({
  units, duplicates, orders, header = true,
}: {
  units: SameAddressUnitDto[];
  duplicates: Map<number, number[]>;
  orders: OpenOrdersState;
  /** Le sous-en-tête ambre qui sépare ces lignes de celles du bâtiment. Inutile dans la carte seule : son titre le dit. */
  header?: boolean;
}) {
  const { t } = useTranslation(['tickets']);
  if (units.length === 0) return null;
  return (
    <>
      {header && (
        <tr className="border-t border-amber-200 bg-amber-100/80">
          <td colSpan={COLS} className="px-3 py-1.5 text-[11px] font-semibold text-amber-800">
            <span className="inline-flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {t('tickets:validation.sameAddress.groupTitle')}
              <span className="font-normal">· {t('tickets:validation.mduGroupUnits', { count: units.length })}</span>
            </span>
          </td>
        </tr>
      )}
      {units.map((u) => <UnitRow key={u.jvid} u={u} twins={duplicates.get(u.jvid)} orders={orders} />)}
    </>
  );
}

/** Ce que la lecture a à dire d'elle-même. Rien pendant qu'elle tourne, rien quand elle a tout lu. */
export function SameAddressNotice({ state, className }: { state: SameAddressState; className?: string }) {
  const { t } = useTranslation(['tickets']);
  if (state.status === 'error') {
    return (
      <p className={cn('flex flex-wrap items-center gap-2 text-[11px] font-medium text-amber-700', className)} role="alert">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        {t('tickets:validation.sameAddress.error')}
        <button
          type="button"
          onClick={state.retry}
          className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2 py-0.5 font-semibold text-amber-800 hover:bg-amber-50"
        >
          <RefreshCw className="h-3 w-3" />
          {t('tickets:validation.sameAddress.retry')}
        </button>
      </p>
    );
  }
  if (state.status === 'ready' && state.truncated) {
    return (
      <p className={cn('flex items-center gap-1.5 text-[11px] text-amber-700', className)}>
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        {t('tickets:validation.sameAddress.truncated')}
      </p>
    );
  }
  return null;
}

/** La phrase qui dit ce que sont ces lignes ambre — au-dessus du tableau, dès qu'il y en a une. */
export function SameAddressBanner({ count, className }: { count: number; className?: string }) {
  const { t } = useTranslation(['tickets']);
  if (count === 0) return null;
  return (
    <p className={cn('flex items-start gap-1.5 text-[11px] font-medium text-amber-700', className)} role="note">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      {t('tickets:validation.sameAddress.banner', { count })}
    </p>
  );
}

/** Le tableau à lui seul : la fenêtre d'un logement seul dans son bâtiment n'a pas de tableau des logements. */
export function SameAddressCard({
  units, duplicates, orders, state,
}: {
  units: SameAddressUnitDto[];
  duplicates: Map<number, number[]>;
  orders: OpenOrdersState;
  state: SameAddressState;
}) {
  const { t } = useTranslation(['tickets']);
  const notice = state.status === 'error' || (state.status === 'ready' && state.truncated);
  if (units.length === 0 && !notice) return null;
  return (
    <GlassCard className="p-4">
      <h2 className="mb-1 flex items-center gap-2 text-sm font-bold text-slate-800">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        {t('tickets:validation.sameAddress.cardTitle')}
      </h2>
      <SameAddressNotice state={state} className="mb-2" />
      {units.length > 0 && (
        <>
          <SameAddressBanner count={units.length} className="mb-2" />
          <div className="overflow-hidden rounded-xl border border-amber-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colJvid')}</th>
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colAddress')}</th>
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colBus')}</th>
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colStatus')}</th>
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colDeliveryStatus')}</th>
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colDeploymentList')}</th>
                  <th className="px-3 py-2 font-semibold">{t('tickets:validation.colBuildList')}</th>
                  <th className="w-8 px-2 py-2 text-center" title={t('tickets:validation.openOrders.colLabel')} aria-label={t('tickets:validation.openOrders.colLabel')}>
                    <ShoppingCart className="inline-block h-3.5 w-3.5" aria-hidden="true" />
                  </th>
                </tr>
              </thead>
              <tbody>
                <SameAddressRows units={units} duplicates={duplicates} orders={orders} header={false} />
              </tbody>
            </table>
          </div>
        </>
      )}
    </GlassCard>
  );
}
